import { Box, BoxProps, Skeleton } from "@mui/material";
import { PropsWithChildren, ReactNode } from "react";
import { MonitoringCardHeader } from "src/components/cards/custom/monitoring-card/MonitoringCardHeader";
import { MonitoringErrorCard } from "src/components/cards/custom/monitoring-card/MonitoringErrorCard";
import { MonitoringCardTopValue } from "src/components/cards/custom/monitoring-card/MonitoringCardTopValue";
import { MonitoringCardBottomArea } from "src/components/cards/custom/monitoring-card/MonitoringCardBottomArea";
import { BasicSquaredCard } from "src/components/cards/deprecated/BasicSquaredCard";

export enum MonitoringCardTypeErrors {
    ERROR_TOP_VALUE = "ERROR_TOP_VALUE",
    ERROR_BOTTOM_VALUE = "ERROR_BOTTOM_VALUE",
    ERROR_GENERAL = "ERROR_GENERAL",
}

export interface MonitoringCardProps extends BoxProps {
    /**
     * Top title for Monitoring Card.
     */
    topTitle: string;

    /**
     * The main value of the Card.
     */
    topValue: string;

    /**
     * Bottom title which will be placed in the bottom left corner.
     */
    bottomTitle?: string;

    /**
     * Bottom value which will be placed in the bottom right corner.
     */
    bottomValue?: string;

    /**
     * Change the unit for the topValue.
     * @default kWh
     */
    topValueUnit?: string;

    /**
     * Optional error type, which will define the error messages.
     */
    errorMessages?: Map<MonitoringCardTypeErrors, string>;

    /**
     * Optional show error type, which will define the error types that will be shown
     */
    errors?: MonitoringCardTypeErrors[];

    /**
     * Optional isLoading state.
     * @default false
     */
    isLoading?: boolean;

    /**
     * The info icon is positioned in the top right corner and has a default height and width of 24px,
     * in mobile view it moves right next to the topTitle
     */
    infoIcon?: ReactNode;
}

/**
 * MonitoringCard used to structure content in a squared card with a padding of 16px.
 *
 * - MUI Spacing based on 8px scaling factor: https://mui.com/material-ui/customization/spacing/
 */

export const MonitoringCard = (props: PropsWithChildren<MonitoringCardProps>) => {
    const { topTitle, topValue, topValueUnit, errors, errorMessages, bottomTitle, bottomValue, infoIcon, isLoading, children } =
        props;

    const monitoringCardContent = (
        <>
            {errors && errors.includes(MonitoringCardTypeErrors.ERROR_GENERAL) ? (
                <MonitoringErrorCard cardProps={props} />
            ) : (
                <>
                    <Box display="flex" flexDirection="column" justifyContent="space-between" overflow="hidden">
                        <MonitoringCardHeader topTitle={topTitle} infoIcon={infoIcon} />
                        <MonitoringCardTopValue
                            topTitle={topTitle}
                            topValue={topValue}
                            topValueUnit={topValueUnit}
                            isLoading={isLoading}
                            error={
                                errors && errorMessages && errors.includes(MonitoringCardTypeErrors.ERROR_TOP_VALUE)
                                    ? errorMessages.get(MonitoringCardTypeErrors.ERROR_TOP_VALUE)
                                    : undefined
                            }
                        />
                    </Box>
                    <Box maxWidth={"100%"} minWidth={0}>
                        {children && (
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    height: bottomTitle || bottomValue ? "32px" : "55px",
                                    maxHeight: bottomTitle || bottomValue ? "32px" : "55px",
                                    position: "relative",
                                }}
                            >
                                {isLoading ? (
                                    <Skeleton
                                        data-testid="skeleton-chart-area"
                                        variant="rounded"
                                        sx={{ height: "100%", width: "100%", borderRadius: "12px" }}
                                    />
                                ) : (
                                    children
                                )}
                            </Box>
                        )}
                        {(bottomTitle || bottomValue) && <MonitoringCardBottomArea {...props} />}
                    </Box>
                </>
            )}
        </>
    );

    return (
        <BasicSquaredCard>
            <Box
                data-testid={"squared-content-wrapper"}
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "grid",
                    alignContent: "space-between",
                }}
            >
                {monitoringCardContent}
            </Box>
        </BasicSquaredCard>
    );
};
